import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useCustomizeLibraryItem } from '../hooks';
import type { LibraryItemDetail, LibraryKind } from '../schemas';
import { UsedByLinks } from './UsedByLinks';
import styles from './SystemReadOnlyView.module.css';

interface SystemReadOnlyViewProps {
  kind: LibraryKind;
  item: Extract<LibraryItemDetail, { kind: 'system' }>;
}

export function SystemReadOnlyView({ kind, item }: SystemReadOnlyViewProps) {
  const navigate = useNavigate();
  const customize = useCustomizeLibraryItem(kind);
  const [error, setError] = useState<string | null>(null);

  const backPath = `/library/${kind === 'position' ? 'positions' : 'units'}`;
  const labels = readOnlyLabelsFor(kind);

  function onCustomize() {
    setError(null);
    customize.mutate(
      { systemRowId: item.id },
      {
        onSuccess: ({ item: newItem, migratedMemberCount }) => {
          void navigate(`${backPath}/${newItem.id}`, {
            state: { customizedFrom: item.name, migratedMemberCount },
            replace: true,
          });
        },
        onError: (err) => setError(formatCustomizeError(err)),
      },
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title={item.name} subtitle={labels.subtitle} />

      <div className={styles.body}>
        <div className={styles.managedRow}>
          <Chip variant="neutral" size="sm">
            {t('Linqage')}
          </Chip>
          <span>{labels.managedNote}</span>
        </div>

        {item.description ? (
          <div className={styles.field}>
            <span className={styles.label}>{t('Description')}</span>
            <p className={styles.readonlyText}>{item.description}</p>
          </div>
        ) : null}

        <UsedByLinks
          agents={item.defaultedByAgents}
          jobs={item.defaultedByJobs}
          kindLabel={kind === 'position' ? t('Position') : t('Department')}
        />

        {error ? (
          <div className={styles.formError} role="alert">
            {error}
          </div>
        ) : null}

        <div className={styles.actions}>
          <Button type="button" variant="ghost" size="md" onClick={() => void navigate(backPath)}>
            {t('Back')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={onCustomize}
            isLoading={customize.isPending}
          >
            {customize.isPending ? t('Customizing…') : labels.customizeButton}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function formatCustomizeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return t('You already have a customized copy of this item.');
    if (err.status === 403) return t("You don't have permission to customize the Library.");
    if (err.status === 404) return t('This item is no longer available.');
    return err.message;
  }
  if (err instanceof NetworkError) {
    return t("Couldn't reach the server. Check your connection and try again.");
  }
  return t('Something went wrong. Please try again.');
}

function readOnlyLabelsFor(kind: LibraryKind): {
  subtitle: string;
  managedNote: string;
  customizeButton: string;
} {
  if (kind === 'position') {
    return {
      subtitle: t('A Position from the Linqage catalog.'),
      managedNote: t('This Position is managed by Linqage and cannot be edited directly.'),
      customizeButton: t('Customize this Position'),
    };
  }
  return {
    subtitle: t('A Department from the Linqage catalog.'),
    managedNote: t('This Department is managed by Linqage and cannot be edited directly.'),
    customizeButton: t('Customize this Department'),
  };
}
